/**
 * Capacity & Expansion — when the airport runs out of room, on which
 * constraint first, and what each way of adding room costs per passenger
 * it admits.
 *
 * Demand comes from the passenger count and the growth the executive
 * enters; capacity from the design figures entered for the terminal, the
 * stands and the runway; the positions that strand traffic from the capacity
 * model. Saturation is arithmetic on those: the year each constraint is
 * reached at the entered growth, and the year it is reached if growth is a
 * third lower or higher. The options carry starting figures the airport
 * replaces, and every one says so.
 *
 * Pure. The page gathers, the desk decides.
 */
import type { CriticalReport } from "./capacity";
import type { SourceRef } from "./decision";
import type { Finance } from "./finance";
import { t } from "../i18n";
import { money } from "./intelligence";
import { findKpi, type Kpi } from "./kpis";
import type { ProjectRow } from "./projects";

export interface ExpansionEntered {
  passengersPerYear?: number;
  growthPct?: number;
  terminalCapacityPax?: number;
  contactStands?: number;
  remoteStands?: number;
  standsNeededAtCapacity?: number;
  runwayMovementsPerHour?: number;
  peakMovementsPerHour?: number;
  options?: Partial<
    Record<string, { capacityPax?: number; stands?: number; cost?: number; months?: number; enabled?: boolean }>
  >;
  enteredBy?: string;
  enteredAt?: string;
}

/** Starting figures, marked as such until the airport replaces them. */
export function startingExpansion(): Required<
  Pick<
    ExpansionEntered,
    | "passengersPerYear"
    | "growthPct"
    | "terminalCapacityPax"
    | "contactStands"
    | "remoteStands"
    | "standsNeededAtCapacity"
    | "runwayMovementsPerHour"
    | "peakMovementsPerHour"
  >
> {
  return {
    passengersPerYear: 9_200_000,
    growthPct: 6,
    terminalCapacityPax: 12_000_000,
    contactStands: 14,
    remoteStands: 10,
    standsNeededAtCapacity: 30,
    runwayMovementsPerHour: 30,
    peakMovementsPerHour: 22,
  };
}

export interface Constraint {
  key: "terminal" | "stands" | "runway";
  label: string;
  capacity: number;
  demand: number;
  unit: string;
  utilisationPct: number;
  /** the year the constraint is reached at the entered growth; null when it never is */
  saturationYear: number | null;
  low: number | null;
  high: number | null;
  assumed: boolean;
  sources: SourceRef[];
}

export interface ExpansionOption {
  key: string;
  name: string;
  meaning: string;
  relieves: Constraint["key"][];
  capacityPax: number;
  stands: number;
  cost: number;
  months: number;
  costPerPax: number | null;
  /** the year construction must start so the room arrives before saturation */
  startBy: number | null;
  door: "one_way" | "two_way";
  assumed: boolean;
  forCase: string;
  againstCase: string;
  sources: SourceRef[];
}

export interface ExpansionInputs {
  now: Date;
  siteCode: string;
  currency: string;
  entered: ExpansionEntered;
  kpis: Kpi[];
  critical: CriticalReport | null;
  projects: ProjectRow[];
  finance: Finance | null;
}

export interface ExpansionDesk {
  generatedAt: string;
  currency: string;
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    text: string;
    tone?: "good" | "bad" | "warn";
    assumed?: boolean;
    sources: SourceRef[];
  }[];
  constraints: Constraint[];
  binding: Constraint | null;
  options: ExpansionOption[];
  recommended: string | null;
  reasoning: string[];
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
}

const yearsTo = (demand: number, capacity: number, growth: number): number | null => {
  if (demand >= capacity) return 0;
  if (growth <= 0) return null;
  return Math.log(capacity / demand) / Math.log(1 + growth / 100);
};

export function buildExpansionDesk(inp: ExpansionInputs): ExpansionDesk {
  const cur = inp.currency;
  const start = startingExpansion();
  const e = inp.entered;
  const v = <K extends keyof typeof start>(k: K): { value: number; assumed: boolean } => ({
    value: (e[k] as number | undefined) ?? start[k],
    assumed: e[k] == null,
  });
  const pax = v("passengersPerYear");
  const growth = v("growthPct");
  const term = v("terminalCapacityPax");
  const contact = v("contactStands");
  const remote = v("remoteStands");
  const standsNeed = v("standsNeededAtCapacity");
  const rwy = v("runwayMovementsPerHour");
  const peak = v("peakMovementsPerHour");
  const year = inp.now.getFullYear() + inp.now.getMonth() / 12;
  const entered = (what: string): SourceRef => ({
    kind: "entered",
    ref: "entered",
    label: what,
    enteredBy: e.enteredBy,
    enteredAt: e.enteredAt,
  });
  const starting = (what: string): SourceRef => ({
    kind: "demo",
    ref: "starting value",
    label: `${what} — replace with the airport's own`,
  });
  const src = (assumed: boolean, what: string) => (assumed ? starting(what) : entered(what));
  const g = growth.value;
  const sat = (demand: number, capacity: number) => {
    const y = yearsTo(demand, capacity, g);
    const lo = yearsTo(demand, capacity, g * 1.33);
    const hi = yearsTo(demand, capacity, g * 0.67);
    return {
      y: y == null ? null : Math.round((year + y) * 10) / 10,
      low: lo == null ? null : Math.round((year + lo) * 10) / 10,
      high: hi == null ? null : Math.round((year + hi) * 10) / 10,
    };
  };
  const stands = contact.value + remote.value;
  const standsDemand = (standsNeed.value * pax.value) / term.value;
  const constraints: Constraint[] = [
    (() => {
      const s_ = sat(pax.value, term.value);
      return {
        key: "terminal" as const,
        label: "Terminal",
        capacity: term.value,
        demand: pax.value,
        unit: "pax/yr",
        utilisationPct: Math.round((1000 * pax.value) / term.value) / 10,
        saturationYear: s_.y,
        low: s_.low,
        high: s_.high,
        assumed: term.assumed || pax.assumed,
        sources: [src(term.assumed, "terminal design capacity"), src(pax.assumed, "passengers a year")],
      };
    })(),
    (() => {
      const s_ = sat(standsDemand, stands);
      return {
        key: "stands" as const,
        label: "Stands",
        capacity: stands,
        demand: Math.round(standsDemand * 10) / 10,
        unit: "stands",
        utilisationPct: Math.round((1000 * standsDemand) / stands) / 10,
        saturationYear: s_.y,
        low: s_.low,
        high: s_.high,
        assumed: contact.assumed || remote.assumed || standsNeed.assumed,
        sources: [
          src(contact.assumed, "contact stands"),
          src(remote.assumed, "remote stands"),
          src(standsNeed.assumed, "stands needed at terminal capacity"),
        ],
      };
    })(),
    (() => {
      const s_ = sat(peak.value, rwy.value);
      return {
        key: "runway" as const,
        label: "Runway",
        capacity: rwy.value,
        demand: peak.value,
        unit: "movements/h",
        utilisationPct: Math.round((1000 * peak.value) / rwy.value) / 10,
        saturationYear: s_.y,
        low: s_.low,
        high: s_.high,
        assumed: rwy.assumed || peak.assumed,
        sources: [src(rwy.assumed, "declared runway capacity"), src(peak.assumed, "peak-hour movements")],
      };
    })(),
  ];
  const binding = [...constraints].sort((a, b) => (a.saturationYear ?? 9999) - (b.saturationYear ?? 9999))[0] ?? null;

  /* options: the platform's own apron project first, then the catalogue */
  const apron = inp.projects.find((p) => /apron/i.test(p.name)) ?? null;
  const apronFin = inp.finance?.capex.find((c) => /apron/i.test(c.name)) ?? null;
  const catalogue: Omit<ExpansionOption, "costPerPax" | "startBy" | "assumed" | "sources">[] = [
    {
      key: "apron",
      name: apron ? apron.name : t("Apron expansion"),
      meaning: t("more stands on the apron; relieves the stand constraint, not the terminal"),
      relieves: ["stands"],
      capacityPax: 0,
      stands: 8,
      cost: apronFin ? apronFin.budget : apron?.money.budgetMinor ? apron.money.budgetMinor / 100 : 145_000_000,
      months: 30,
      door: "one_way",
      forCase: t("Already in the capital program; stands are the constraint the capacity model can see."),
      againstCase: t("Does nothing for the terminal, which saturates on its own curve."),
    },
    {
      key: "remote",
      name: t("Remote stands with bussing"),
      meaning: t("paved remote positions and a bussing operation; quick, but every passenger on them is bussed"),
      relieves: ["stands"],
      capacityPax: 0,
      stands: 6,
      cost: 20_000_000,
      months: 12,
      door: "two_way",
      forCase: t("A year, not three; reversible if the apron follows."),
      againstCase: t("Bussing is a passenger cost and a queue-KPI cost every day it runs."),
    },
    {
      key: "pier",
      name: t("Terminal pier extension"),
      meaning: t("a pier with contact stands and gate rooms; relieves the terminal and the stands together"),
      relieves: ["terminal", "stands"],
      capacityPax: 3_000_000,
      stands: 8,
      cost: 480_000_000,
      months: 42,
      door: "one_way",
      forCase: t("The only option that moves the terminal constraint."),
      againstCase: t(
        "The largest cash call and the longest build; must start before the terminal saturates, not when.",
      ),
    },
    {
      key: "spread",
      name: t("Spread the peak with the airlines"),
      meaning: t(
        "schedule coordination that moves movements out of the peak hour; adds effective capacity without concrete",
      ),
      relieves: ["runway", "stands"],
      capacityPax: 0,
      stands: 2,
      cost: 2_000_000,
      months: 6,
      door: "two_way",
      forCase: t("Cheapest room there is; the capacity model shows which positions have slack."),
      againstCase: t("Depends on airlines agreeing to slots they did not ask for."),
    },
    {
      key: "processing",
      name: t("Faster processing in the terminal"),
      meaning: t(
        "self-service bag drop, e-gates and more screening lanes; raises what the building handles without extending it",
      ),
      relieves: ["terminal"],
      capacityPax: 1_200_000,
      stands: 0,
      cost: 60_000_000,
      months: 18,
      door: "two_way",
      forCase: t("Moves the terminal constraint at a tenth of a pier's cost."),
      againstCase: t("A ceiling, not a floor: the building's curb, hold rooms and gates still bind."),
    },
  ];
  const options: ExpansionOption[] = catalogue.map((o) => {
    const ov = e.options?.[o.key] ?? {};
    const capacityPax = ov.capacityPax ?? o.capacityPax;
    const stands_ = ov.stands ?? o.stands;
    const cost = ov.cost ?? o.cost;
    const months = ov.months ?? o.months;
    const assumed = ov.cost == null && !(o.key === "apron" && (apronFin || apron?.money.budgetMinor));
    /* effective passengers admitted: terminal capacity added, else stands × the passengers a stand serves */
    const paxPerStand = term.value / standsNeed.value;
    const effective = capacityPax || stands_ * paxPerStand;
    const relieved = constraints
      .filter((c) => o.relieves.includes(c.key))
      .sort((a, b) => (a.saturationYear ?? 9999) - (b.saturationYear ?? 9999))[0];
    const startBy =
      relieved?.saturationYear != null ? Math.round((relieved.saturationYear - months / 12) * 10) / 10 : null;
    return {
      ...o,
      capacityPax,
      stands: stands_,
      cost,
      months,
      costPerPax: effective > 0 ? Math.round(cost / effective) : null,
      startBy,
      assumed,
      sources: [
        ...(o.key === "apron" && apron
          ? [{ kind: "project" as const, ref: apron.code, label: apron.name, href: `/capital-projects/${apron.id}` }]
          : []),
        ...(o.key === "apron" && apronFin
          ? [
              {
                kind: "demo" as const,
                ref: "demo finance feed",
                label: `${apronFin.name} budget — demonstration figures`,
              },
            ]
          : []),
        ov.cost != null ? entered(`${o.name} figures`) : starting(`${o.name} figures`),
      ],
    };
  });
  const enabled = options.filter((o) => e.options?.[o.key]?.enabled !== false);
  /* recommend: what relieves the binding constraint at the least cost per passenger, and starts in time */
  const forBinding = enabled.filter((o) => binding && o.relieves.includes(binding.key) && o.costPerPax != null);
  const ranked = [...forBinding].sort((a, b) => (a.costPerPax ?? Infinity) - (b.costPerPax ?? Infinity));
  const rec = ranked[0] ?? null;
  const late = rec && rec.startBy != null && rec.startBy < year;
  const reasoning = [
    binding
      ? binding.saturationYear != null
        ? t(
            "{constraint} binds first: {pct}% used today, saturating in {year} at {growth}% growth ({high} at a third less, {low} at a third more).",
            {
              constraint: t(binding.label),
              pct: binding.utilisationPct,
              year: binding.saturationYear,
              growth: g,
              high: binding.high ?? "—",
              low: binding.low ?? "—",
            },
          )
        : t(
            "{constraint} binds first: {pct}% used today, saturating never at this growth at {growth}% growth ({high} at a third less, {low} at a third more).",
            {
              constraint: t(binding.label),
              pct: binding.utilisationPct,
              growth: g,
              high: binding.high ?? "—",
              low: binding.low ?? "—",
            },
          )
      : t("No constraint entered."),
    ...ranked.map((o) =>
      t("{name}: {cost} for {adds} — {perPax}, {months} months, start by {startBy}.", {
        name: o.name,
        cost: money(o.cost, cur),
        adds: o.capacityPax
          ? t("{n} passengers a year", { n: o.capacityPax.toLocaleString("en") })
          : t("{n} stands", { n: o.stands }),
        perPax:
          o.costPerPax != null
            ? t("{amount} a passenger", { amount: money(o.costPerPax, cur) })
            : t("not priced per passenger"),
        months: o.months,
        startBy: o.startBy ?? "—",
      }),
    ),
    ...(late
      ? [
          t(
            "{name} would need to have started in {year}: the room arrives after the constraint binds, so the two-way options buy the time.",
            { name: rec!.name, year: rec!.startBy! },
          ),
        ]
      : []),
  ];
  const crit = inp.critical;
  const paxKpi = findKpi(inp.kpis, "pax");
  const queue = findKpi(inp.kpis, "queue");
  const tiles: ExpansionDesk["tiles"] = [
    {
      key: "pax",
      label: t("passengers a year"),
      value: pax.value,
      unit: "",
      text: paxKpi
        ? t("growing {growth}% a year; {pct}% of plan this period", { growth: g, pct: paxKpi.value ?? "—" })
        : t("growing {growth}% a year", { growth: g }),
      assumed: pax.assumed,
      sources: [
        src(pax.assumed, "passengers a year"),
        ...(paxKpi ? [{ kind: "kpi" as const, ref: "pax", label: paxKpi.label, href: "/executive-kpis" }] : []),
      ],
    },
    ...constraints.map((c) => ({
      key: c.key,
      label: t("{constraint} used", { constraint: t(c.label.toLowerCase()) }),
      value: c.utilisationPct,
      unit: "%",
      text:
        c.saturationYear != null
          ? t("{demand} of {capacity} {unit}; saturates in {year}", {
              demand: c.demand.toLocaleString("en"),
              capacity: c.capacity.toLocaleString("en"),
              unit: t(c.unit),
              year: c.saturationYear,
            })
          : t("{demand} of {capacity} {unit}; saturates never at this growth", {
              demand: c.demand.toLocaleString("en"),
              capacity: c.capacity.toLocaleString("en"),
              unit: t(c.unit),
            }),
      tone: (c.utilisationPct >= 90 ? "bad" : c.utilisationPct >= 75 ? "warn" : "good") as "bad" | "warn" | "good",
      assumed: c.assumed,
      sources: c.sources,
    })),
    {
      key: "critical",
      label: t("positions that strand traffic"),
      value: crit?.known ? crit.critical.length : null,
      unit: "",
      text: crit?.known
        ? t("{critical}; flexible: {flexible}", {
            critical: crit.critical.map((p) => p.position).join(", ") || t("none"),
            flexible: crit.flexible.map((p) => p.position).join(", ") || t("none"),
          })
        : t("unknown: {reason}", { reason: crit?.whyNot ?? t("no capacity model") }),
      tone: crit?.known && crit.critical.length ? "warn" : "good",
      sources: crit
        ? [{ kind: "capacity", ref: "critical positions", label: "the next 90 days", href: "/capacity" }]
        : [],
    },
    {
      key: "queue",
      label: t("worst queue"),
      value: queue?.value ?? null,
      unit: queue?.unit ?? "",
      text: queue
        ? t("target {target} — the terminal constraint as passengers feel it", { target: queue.target ?? "—" })
        : t("no queue KPI"),
      tone: queue && queue.value != null && queue.target != null && queue.value > queue.target ? "bad" : "good",
      sources: queue ? [{ kind: "kpi", ref: "queue", label: queue.label, href: "/executive-kpis" }] : [],
    },
  ];
  const boardLine = [
    binding
      ? binding.saturationYear != null
        ? t("{site} runs out of {constraint} first: {pct}% used, saturating in {year} at {growth}% growth.", {
            site: inp.siteCode,
            constraint: t(binding.label.toLowerCase()),
            pct: binding.utilisationPct,
            year: binding.saturationYear,
            growth: g,
          })
        : t(
            "{site} runs out of {constraint} first: {pct}% used, saturating never at this growth at {growth}% growth.",
            { site: inp.siteCode, constraint: t(binding.label.toLowerCase()), pct: binding.utilisationPct, growth: g },
          )
      : t("No constraint entered."),
    rec
      ? rec.costPerPax != null
        ? t("{name} relieves it at {cost}, {perPax} a passenger, and must start by {startBy}.", {
            name: rec.name,
            cost: money(rec.cost, cur),
            perPax: money(rec.costPerPax, cur),
            startBy: rec.startBy ?? "—",
          })
        : t("{name} relieves it at {cost}, unpriced per passenger, and must start by {startBy}.", {
            name: rec.name,
            cost: money(rec.cost, cur),
            startBy: rec.startBy ?? "—",
          })
      : t("No option relieves the binding constraint."),
    late
      ? t(
          "That start date has passed: the two-way options — spreading the peak, remote stands, faster processing — buy the time the build needs.",
        )
      : [pax, growth, term, contact, remote, standsNeed, rwy, peak].some((x) => x.assumed)
        ? t("The figures are starting values until the airport's own are entered.")
        : t("The figures are the airport's own."),
  ];
  const material = materialForExpansion({
    tiles,
    constraints,
    binding,
    options,
    recommended: rec?.key ?? null,
    reasoning,
    boardLine,
    currency: cur,
  } as ExpansionDesk);
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));
  return {
    generatedAt: inp.now.toISOString(),
    currency: cur,
    tiles,
    constraints,
    binding,
    options,
    recommended: rec?.key ?? null,
    reasoning,
    boardLine,
    gaps: [
      ...(crit?.known ? [] : [t("the capacity model does not cover the next 90 days")]),
      ...([pax, growth, term, contact, remote, standsNeed, rwy, peak].some((x) => x.assumed)
        ? [t("demand and capacity figures are starting values until entered")]
        : []),
    ],
    figures,
  };
}

export const EXPANSION_MODEL_PROMPT = [
  "You are advising an airport's chief executive on capacity and expansion from the material given and nothing else.",
  "You may explain, compare and argue; you must not state any number that is not in the material.",
  "Say when a figure is a starting value or an entered value rather than a platform record. Be brief and direct.",
].join(" ");

export function materialForExpansion(
  d: Pick<
    ExpansionDesk,
    "tiles" | "constraints" | "binding" | "options" | "recommended" | "reasoning" | "boardLine" | "currency"
  >,
): string {
  const L: string[] = [];
  for (const t of d.tiles)
    L.push(`Tile ${t.label}: ${t.value ?? "n/a"} ${t.unit} — ${t.text}${t.assumed ? " [starting/entered]" : ""}`);
  for (const c of d.constraints)
    L.push(
      `Constraint ${c.key}: demand ${c.demand} of ${c.capacity} ${c.unit} (${c.utilisationPct}%), saturates ${c.saturationYear ?? "never"} (range ${c.high ?? "—"} to ${c.low ?? "—"})${c.assumed ? " [starting]" : ""}`,
    );
  L.push(`Binding: ${d.binding?.key ?? "none"}.`);
  for (const o of d.options)
    L.push(
      `Option ${o.key} "${o.name}": ${o.meaning}. Relieves ${o.relieves.join(", ")}. ${o.capacityPax} passengers a year, ${o.stands} stands, ${o.cost} ${d.currency}, ${o.months} months, ${o.costPerPax ?? "n/a"} per passenger, start by ${o.startBy ?? "n/a"}, ${o.door.replace("_", "-")}${o.assumed ? " [starting figures]" : ""}. For: ${o.forCase} Against: ${o.againstCase}`,
    );
  L.push(`Recommended: ${d.recommended ?? "none"}. ${d.reasoning.join(" ")}`);
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerExpansion(q: string, d: ExpansionDesk): string | null {
  const t = q.toLowerCase();
  if (/when|run out|saturat|bind/.test(t))
    return d.constraints
      .map(
        (c) =>
          `${c.label}: ${c.utilisationPct}% used, saturates ${c.saturationYear != null ? `in ${c.saturationYear}` : "never at this growth"} (${c.high ?? "—"}–${c.low ?? "—"} across growth)`,
      )
      .join("\n");
  if (/recommend|which|first|cheapest/.test(t)) return d.reasoning.join(" ");
  if (/board|summar/.test(t)) return d.boardLine.join(" ");
  const o = d.options.find((x) => t.includes(x.key) || t.includes(x.name.toLowerCase().slice(0, 10)));
  if (o)
    return `${o.name}: ${o.meaning}. ${money(o.cost, d.currency)}, ${o.months} months, ${o.costPerPax != null ? `${money(o.costPerPax, d.currency)} a passenger` : "unpriced per passenger"}, start by ${o.startBy ?? "—"}. For: ${o.forCase} Against: ${o.againstCase}`;
  return null;
}
